import * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import Modal from "@/components/common/Modal";
import { useUpdateTask } from "../../hooks/tasks.hooks";
import { useStaff } from "@/components/pages/staff/hooks/useStaff";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useTeams } from "@/components/pages/staff/hooks/useTeams";
import { Staff } from "@/types/tasks";
import { useSelectedHotelId } from "@/utils/hotelContext";

interface Props {
  taskId: string;
  assignedStaffId?: string;
  teamId?: string;
  trigger: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  externalChangeHandler?: (assignment: {
    staffId?: string;
    teamStaffId?: string;
  }) => void;
}

const AssignStaffModal: React.FC<Props> = ({
  taskId,
  assignedStaffId,
  teamId,
  trigger,
  open,
  onOpenChange,
  externalChangeHandler,
}) => {
  const { userProfile } = useAuthenticatedUser();
  const selectedHotelId = useSelectedHotelId();
  const hotelId = selectedHotelId ?? userProfile?.profile?.hotelId;

  const { data: staffData, isLoading } = useStaff({
    hotelId: hotelId as string,
  });
  const { data: teamsData, isLoading: isTeamsLoading } = useTeams({
    isActive: true,
    perPage: 100,
    sortBy: null,
    sortOrder: null,
  });

  const { mutate: updateTask } = useUpdateTask();

  const [selectedStaffId, setSelectedStaffId] = React.useState<string>(
    assignedStaffId || "",
  );
  const [selectedTeamId, setSelectedTeamId] = React.useState<string>(
    teamId || "",
  );

  React.useEffect(() => {
    setSelectedStaffId(assignedStaffId || "");
    setSelectedTeamId(teamId || "");
  }, [assignedStaffId, teamId, open]);

  const handleAssignmentChange = ({
    staffId,
    teamStaffId,
  }: {
    staffId?: string;
    teamStaffId?: string;
  }) => {
    if (externalChangeHandler) {
      externalChangeHandler({
        staffId,
        teamStaffId,
      });
    } else {
      const data = staffId ? { staffId } : { teamStaffId };

      updateTask({
        id: taskId,
        data,
      });
    }

    if (onOpenChange) {
      onOpenChange(false);
    }
  };

  const staffMembers =
    staffData?.users.flatMap((user) => {
      if (!user.staff) {
        return [];
      }

      return [
        {
          staffId: user.staff.id,
          userId: user.id,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          staffName:
            `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
            user.email,
          email: user.email,
        },
      ];
    }) || [];

  return (
    <Modal
      title="Assign Staff or Team"
      trigger={trigger}
      open={open}
      onOpenChange={onOpenChange}
      className="flex flex-col items-center justify-center py-6"
    >
      <div className="w-full max-w-sm">
        <Select
          value={selectedStaffId}
          onValueChange={(newStaffId) => {
            const selectedStaff = staffMembers.find(
              (staff) => staff.staffId === newStaffId,
            );

            setSelectedStaffId(newStaffId);
            setSelectedTeamId("");

            if (externalChangeHandler) {
              handleAssignmentChange({
                staffId: newStaffId,
              });
              return;
            }

            const optimisticStaff: Staff | null = selectedStaff
              ? {
                  id: selectedStaff.userId,
                  profile: {
                    id: selectedStaff.userId,
                    firstName: selectedStaff.firstName,
                    lastName: selectedStaff.lastName,
                    email: selectedStaff.email,
                  },
                }
              : null;

            updateTask({
              id: taskId,
              data: { staffId: newStaffId },
              optimisticStaff,
            });

            if (onOpenChange) {
              onOpenChange(false);
            }
          }}
          disabled={isLoading}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={isLoading ? "Loading..." : "Select staff member"}
            />
          </SelectTrigger>
          <SelectContent>
            {staffMembers.map((staff) => (
              <SelectItem key={staff.staffId} value={staff.staffId}>
                {staff.staffName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="my-3 text-sm text-stone-500">or</div>

      <div className="w-full max-w-sm">
        <Select
          value={selectedTeamId}
          onValueChange={(newTeamId) => {
            setSelectedTeamId(newTeamId);
            setSelectedStaffId("");
            handleAssignmentChange({
              teamStaffId: newTeamId,
            });
          }}
          disabled={isTeamsLoading}
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={isTeamsLoading ? "Loading..." : "Select team"}
            />
          </SelectTrigger>
          <SelectContent>
            {teamsData?.data?.map((team) => (
              <SelectItem key={team.id} value={team.id}>
                {team.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </Modal>
  );
};

export default AssignStaffModal;
